import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '';

// Load an external script once (dedup by id), resolving only once it has
// ACTUALLY loaded. If the element already exists but hasn't finished loading
// yet (a re-render fired mid-load), we must wait for its load event rather
// than resolve immediately — otherwise the caller runs before window.google /
// window.FB exists and silently bails, leaving the button un-rendered.
function loadScript(id, src) {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id);
    if (existing) {
      if (existing.dataset.loaded === 'true') return resolve();
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`failed to load ${src}`)), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.id = id;
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = () => { s.dataset.loaded = 'true'; resolve(); };
    s.onerror = () => reject(new Error(`failed to load ${src}`));
    document.body.appendChild(s);
  });
}

// The provider global (window.google.accounts.id / window.FB) can attach a tick
// after the script's load event. Poll briefly so we don't miss it.
function waitFor(getter, tries = 20) {
  return new Promise((resolve, reject) => {
    const tick = (n) => {
      const val = getter();
      if (val) return resolve(val);
      if (n <= 0) return reject(new Error('provider global never appeared'));
      setTimeout(() => tick(n - 1), 100);
    };
    tick(tries);
  });
}

/**
 * "Continue with Google / Facebook" buttons. Each renders only if its client ID
 * is configured (REACT_APP_GOOGLE_CLIENT_ID / REACT_APP_FACEBOOK_APP_ID). On
 * success it calls onAuth(provider, providerToken) — the parent exchanges that
 * for our JWT via AuthContext.socialLogin.
 */
const SocialLoginButtons = ({ onAuth, disabled }) => {
  const googleBtnRef = useRef(null);
  const [fbReady, setFbReady] = useState(false);

  // Keep the latest onAuth in a ref so the init effects can run exactly ONCE
  // (empty deps) without going stale — otherwise a new onAuth identity on every
  // parent render re-runs the effect and cancels the in-flight script load.
  const onAuthRef = useRef(onAuth);
  useEffect(() => { onAuthRef.current = onAuth; }, [onAuth]);

  // Google Identity Services — renders the official button, returns an ID token.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;
    loadScript('google-gsi', 'https://accounts.google.com/gsi/client')
      .then(() => waitFor(() => window.google?.accounts?.id))
      .then((gid) => {
        if (cancelled || !googleBtnRef.current) return;
        gid.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp) => resp?.credential && onAuthRef.current('google', resp.credential),
        });
        gid.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          width: 320,
          text: 'continue_with',
          logo_alignment: 'center',
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Facebook SDK.
  useEffect(() => {
    if (!FACEBOOK_APP_ID) return;
    let cancelled = false;
    loadScript('facebook-jssdk', 'https://connect.facebook.net/en_US/sdk.js')
      .then(() => waitFor(() => window.FB))
      .then((FB) => {
        if (cancelled) return;
        FB.init({ appId: FACEBOOK_APP_ID, cookie: false, xfbml: false, version: 'v19.0' });
        setFbReady(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleFacebook = () => {
    if (!window.FB) return;
    window.FB.login((response) => {
      const token = response?.authResponse?.accessToken;
      if (token) onAuth('facebook', token);
    }, { scope: 'email,public_profile' });
  };

  if (!GOOGLE_CLIENT_ID && !FACEBOOK_APP_ID) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ my: 2 }}>
        <Typography variant="caption" color="text.secondary">or continue with</Typography>
      </Divider>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        {GOOGLE_CLIENT_ID && (
          <Box ref={googleBtnRef} sx={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }} />
        )}
        {FACEBOOK_APP_ID && (
          <Button
            fullWidth
            variant="outlined"
            onClick={handleFacebook}
            disabled={disabled || !fbReady}
            sx={{
              maxWidth: 320, textTransform: 'none', fontWeight: 600,
              borderColor: '#1877F2', color: '#1877F2',
              '&:hover': { borderColor: '#1877F2', backgroundColor: 'rgba(24,119,242,0.04)' },
            }}
          >
            Continue with Facebook
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default SocialLoginButtons;
